import { useState } from 'react';
import { Button, Drawer, InlineError, Input, Textarea } from '../../components/ui';
import { CategoryField, DirectionField, OriginField } from './LeadFieldInputs';
import { useAddLeadsToList, useCompanies, useCreateLead, useImportLeads } from './hooks';
import { matchCompany } from './companyMatch';
import { splitForList } from './leadListSafety';
import {
  useClosureReasons,
  useLeadCategories,
  useLeadSources,
  useSetCategoriesForMany,
  useSetLeadCategories,
} from './leadFields';
import { newLeadStage } from './stageKeys';
import { parseLeadImport, salesErrorMessage } from './types';
import type { Lead, LeadStage } from './types';

export function ImportDrawer({
  open,
  leads,
  stages,
  listId,
  onClose,
}: {
  open: boolean;
  leads: Lead[];
  stages: LeadStage[];
  /** When set, everyone in the CSV (new AND already known) joins this list. */
  listId?: string;
  onClose: () => void;
}) {
  const importLeads = useImportLeads();
  const addToList = useAddLeadsToList();
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');

  const parsed = parseLeadImport(text, leads);
  const coldStage = newLeadStage(stages);

  // What the WHOLE batch shares. Kyu: "Paste-import batches apply one shared
  // Direction, Source, and Category selection." A pasted list comes from one
  // place — a Typeform export, a conference badge scan — so asking per row
  // would mean nobody sets it at all.
  const [batchDirection, setBatchDirection] = useState<'inbound' | 'outbound' | null>(null);
  const [batchOriginId, setBatchOriginId] = useState<string | null>(null);
  const importSources = useLeadSources();
  const [batchCategoryIds, setBatchCategoryIds] = useState<string[]>([]);
  const importCategories = useLeadCategories();
  // Only to NAME the omission — 20261220000100 is what enforces it.
  const closureReasons = useClosureReasons();
  const setManyCategories = useSetCategoriesForMany();

  // Existing leads whose email appears in the pasted CSV — for list joins.
  const existingMatchIds =
    listId === undefined
      ? []
      : leads
          .filter(
            (lead) => lead.email !== '' && text.toLowerCase().includes(lead.email.toLowerCase()),
          )
          .map((lead) => lead.id);

  function joinList(newIds: string[]) {
    if (listId === undefined) {
      return;
    }
    // THE DOOR DROP 38 MISSED, and the reason 20261220000100 exists.
    //
    // This path matches every existing lead whose email appears anywhere in
    // the pasted text and upserts all of them — in bulk, with no searching.
    // Drop 38's guard reasoned that the unguarded doors were narrow because
    // "reaching the mutation meant searching for somebody by name". This one
    // is a paste box on the Lists tab, and it was wide open.
    //
    // The database refuses those rows now. This is only the sentence that
    // says so: without it a 200-row import silently becomes 199 and nobody
    // ever learns which name went missing or why.
    const wanted = [...newIds, ...existingMatchIds];
    const blocked = splitForList(leads, new Set(existingMatchIds), closureReasons.data ?? []);
    addToList.mutate(
      { listId, leadIds: wanted },
      {
        onSuccess: () => {
          if (blocked !== null && blocked.blocked > 0) {
            setMessage(
              `${String(blocked.blocked)} of these are marked Do not contact and were left off the list.`,
            );
          }
        },
        onError: (cause) => setMessage(salesErrorMessage(cause.message)),
      },
    );
  }

  function run() {
    if (parsed.error !== '') {
      setMessage(parsed.error);
      return;
    }
    if (parsed.newLeads.length === 0) {
      if (listId !== undefined && existingMatchIds.length > 0) {
        joinList([]);
        setMessage(
          `Nothing new to create — added ${existingMatchIds.length} known leads to the list.`,
        );
        setText('');
        return;
      }
      setMessage('Nothing new to import.');
      return;
    }
    if (coldStage === undefined) {
      setMessage('Stages are still loading. Try again.');
      return;
    }
    if (coldStage === null) {
      // Was `?? stages[0]`, which filed every imported lead under whichever
      // status happened to sort first when the vocabulary was wrong. A
      // refusal that names the problem beats a silent misfiling of a batch.
      setMessage('The lead statuses are not loaded, so nothing can be imported yet.');
      return;
    }
    setMessage('');
    importLeads.mutate(
      {
        rows: parsed.newLeads,
        stageId: coldStage.id,
        defaults: { direction: batchDirection, leadSourceId: batchOriginId },
      },
      {
        onSuccess: (result) => {
          joinList(result.ids);
          // Categories are a join table, so they follow the insert. Only the
          // leads this import created — never the already-known ones it
          // merely added to a list, whose categories are somebody else's.
          if (batchCategoryIds.length > 0 && result.ids.length > 0) {
            setManyCategories.mutate({ leadIds: result.ids, categoryIds: batchCategoryIds });
          }
          setMessage(
            `Imported ${result.count} leads.` +
              (listId === undefined
                ? ` Duplicates skipped: ${parsed.duplicates}.`
                : ` Added ${result.count + existingMatchIds.length} to the list.`),
          );
          setText('');
        },
        onError: (cause) => setMessage(salesErrorMessage(cause.message)),
      },
    );
  }

  if (!open) return null;
  return (
    <Drawer
      open
      title="Import leads from CSV"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={run} disabled={importLeads.isPending || text.trim() === ''}>
            {importLeads.isPending ? 'Importing…' : `Import ${parsed.newLeads.length} new leads`}
          </Button>
        </>
      }
    >
      <p
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-secondary)',
        }}
      >
        Paste CSV rows with a header line. Recognized columns: First Name, Last Name, Email, Phone,
        Company, Title. Emails already in the tracker are skipped automatically.
      </p>
      <div className="import-batch-fields">
        <p className="tracker-field-note">
          These two apply to every lead in this paste. Already-known leads are not changed — only
          the ones this import creates.
        </p>
        <DirectionField value={batchDirection} onChange={setBatchDirection} />
        <OriginField
          value={batchOriginId}
          leadSource={batchDirection}
          sources={importSources.data ?? []}
          onChange={setBatchOriginId}
        />
        <CategoryField
          selected={batchCategoryIds}
          onChange={setBatchCategoryIds}
          categories={importCategories.data ?? []}
        />
      </div>
      <Textarea
        label="CSV"
        rows={10}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={
          'First Name,Last Name,Email,Company\nAda,Lovelace,ada@example.com,Analytical Engines'
        }
      />
      {text.trim() !== '' &&
        (parsed.error !== '' ? (
          <InlineError>{parsed.error}</InlineError>
        ) : (
          <p
            style={{
              margin: 'var(--space-3) 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            Ready: {parsed.newLeads.length} new · {parsed.duplicates} duplicates skipped
            {parsed.skipped > 0 ? ` · ${parsed.skipped} empty rows ignored` : ''}
          </p>
        ))}
      {message !== '' && (
        <p
          style={{
            margin: 'var(--space-3) 0 0',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          {message}
        </p>
      )}
    </Drawer>
  );
}

export function NewLeadDrawer({
  open,
  stages,
  initialCompany,
  initialCompanyId,
  onClose,
}: {
  open: boolean;
  stages: LeadStage[];
  initialCompany?: string;
  initialCompanyId?: string;
  onClose: () => void;
}) {
  const create = useCreateLead();
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState(initialCompany ?? '');
  const [title, setTitle] = useState('');
  // Batch 1b. Direction starts blank on purpose: inbound is a consent record
  // the database will not let anybody change afterwards, so it is asked
  // rather than assumed.
  const [direction, setDirection] = useState<'inbound' | 'outbound' | null>(null);
  const [originId, setOriginId] = useState<string | null>(null);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const newLeadSources = useLeadSources();
  const categories = useLeadCategories();
  // Every company already on file, for the suggestion list under the Company
  // field. See `matchedCompany` below for what typing one of them does.
  const knownCompanies = useCompanies();
  const setCategories = useSetLeadCategories();

  const coldStage = newLeadStage(stages);

  // The company this name IS, or null. In its own module because the rule has
  // to stay identical to the corrective SQL pass — a lead created here and a
  // lead repaired by that pass must land on the SAME record, or the cleanup
  // creates the duplicates it was run to prevent. See companyMatch.ts.
  const matchedCompany = matchCompany(companyName, knownCompanies.data ?? []);

  function submit() {
    if (firstName.trim() === '' && email.trim() === '') {
      setError('Give the lead a name or an email first.');
      return;
    }
    if (coldStage === null) {
      // `=== undefined` never fired: the old expression ended in `?? stages[0]`,
      // so it was undefined only when there were no statuses at all. It is now
      // null whenever the vocabulary has no 'new', which is the case worth
      // refusing.
      setError('Lead statuses are not loaded, so a new lead has nowhere to start.');
      return;
    }
    setError('');
    create.mutate(
      {
        firstName,
        lastName,
        email,
        phone: '',
        companyName,
        title,
        stageId: coldStage.id,
        direction,
        leadSourceId: originId,
        // The company link, from whichever source knows it.
        //
        // `matchedCompany` first: if what was typed IS a company on file, the
        // lead joins that record rather than carrying loose text. This is the
        // source of the problem Kyu asked to stop — 69 leads had a typed
        // company name with no record behind it, and Lazzara Orthodontics was
        // one, which is why it never appeared in the CG client-link dropdown.
        //
        // `initialCompanyId` second, and only while the field still says what
        // it said when the drawer opened: retyping the company away must not
        // keep a link to the old one.
        ...(matchedCompany !== null
          ? { companyId: matchedCompany.id }
          : initialCompanyId !== undefined &&
              companyName.trim().toLowerCase() === (initialCompany ?? '').trim().toLowerCase()
            ? { companyId: initialCompanyId }
            : {}),
      },
      {
        onSuccess: (lead) => {
          // Categories are a join table, so they land after the lead exists.
          // A failure here leaves a lead with no categories rather than no
          // lead, which is the recoverable direction — they can be set from
          // the lead's own panel.
          if (categoryIds.length > 0) {
            setCategories.mutate({ leadId: lead.id, categoryIds });
          }
          setFirstName('');
          setLastName('');
          setEmail('');
          setCompanyName('');
          setTitle('');
          setDirection(null);
          setOriginId(null);
          setCategoryIds([]);
          onClose();
        },
        onError: (cause) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  if (!open) return null;
  return (
    <Drawer
      open
      title="New lead"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add lead'}
          </Button>
        </>
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
      <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      {/* Kyu, 1 Sep: "to avoid company typos, especially when adding a new
          lead in a company where there is already another older lead
          registered under that company — auto suggest from database when
          typing."

          The suggestions stop the typos. The MATCH underneath is the fix for
          the bigger problem: typing a known company joins the lead to that
          record, so the link exists from the moment the lead does. "The Pur
          Compoany" happened because this was a free text box with nothing to
          compare against. */}
      <Input
        label="Company"
        value={companyName}
        list="known-companies"
        onChange={(e) => setCompanyName(e.target.value)}
      />
      <datalist id="known-companies">
        {(knownCompanies.data ?? []).map((company) => (
          <option key={company.id} value={company.name} />
        ))}
      </datalist>
      {companyName.trim() === '' ? null : matchedCompany === null ? (
        // Said plainly rather than blocked. Plenty of real leads are at a
        // company nobody has recorded yet, and refusing them would push people
        // to type something close enough — which is how the typos started.
        <p className="tracker-field-note">
          No company record under that name yet. The lead saves fine; somebody can create the
          company later.
        </p>
      ) : (
        <p className="tracker-field-note">
          Linking to <strong>{matchedCompany.name}</strong>.
        </p>
      )}
      <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <DirectionField value={direction} onChange={setDirection} />
      <OriginField
        value={originId}
        leadSource={direction}
        sources={newLeadSources.data ?? []}
        onChange={setOriginId}
      />
      <CategoryField
        selected={categoryIds}
        onChange={setCategoryIds}
        categories={categories.data ?? []}
      />
    </Drawer>
  );
}

// ── Main TrackerPage ──────────────────────────────────────────────────────────
